import { FC, useEffect, useState } from "react";
import { ProfileDocument } from "@/data/interfaces/ProfileDocument";
import profileDocumentService from "@/services/profileDocumentService";
import { ResultItem } from "./ResultItem";
import { UploadDocumentModal } from "./UploadDocumentModal";
import { ErrorMessage } from "@/components/UI";
import { Droplet, Dna, Package, FileText, ChevronDown, Upload } from "lucide-react";

const INITIAL_COUNT = 4;

const TYPE_ICONS: Record<string, React.ReactNode> = {
  blood: <Droplet className="h-5 w-5 text-[#6935B1]" />,
  genome: <Dna className="h-5 w-5 text-[#6935B1]" />,
  "complete bundle": <Package className="h-5 w-5 text-[#6935B1]" />,
};

const getIcon = (type: string) =>
  TYPE_ICONS[type.toLowerCase()] || <FileText className="h-5 w-5 text-[#6935B1]" />;

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

export const ResultsPanel: FC = () => {
  const [documents, setDocuments] = useState<ProfileDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [showAll, setShowAll] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(undefined);
      const data = await profileDocumentService.getDocuments();
      setDocuments(data);
    } catch (error) {
      console.error(error);
      setErrorMessage(`Failed to load results: ${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const visible = showAll ? documents : documents.slice(0, INITIAL_COUNT);

  if (isLoading) {
    return (
      <div className="w-[413px] rounded-2xl bg-white p-6 shadow-sm">
        <h3 className="mb-6 text-lg font-semibold text-gray-900">Results</h3>
        <div className="text-center text-gray-500">Loading results...</div>
      </div>
    );
  }

  return (
    <>
      <div className="w-[413px] rounded-2xl bg-white p-6 shadow-sm" style={{ minHeight: "279px" }}>
        <div className="mb-6 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Results</h3>
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-1.5 rounded-lg bg-[#6935B1]/10 px-3 py-1.5 text-xs font-medium text-[#6935B1] transition-colors hover:bg-[#6935B1]/20"
          >
            <Upload className="h-3.5 w-3.5" />
            Upload
          </button>
        </div>

        <ErrorMessage error={errorMessage} />

        {documents.length > 0 ? (
          <div className="flex flex-col gap-6">
            {visible.map((doc) => (
              <ResultItem
                key={doc.id}
                title={doc.type}
                date={formatDate(doc.created_at)}
                icon={getIcon(doc.type)}
              />
            ))}

            {documents.length > INITIAL_COUNT && (
              <button
                onClick={() => setShowAll(!showAll)}
                className="mx-auto flex items-center gap-1 rounded-full border border-gray-200 px-4 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-50"
              >
                {showAll ? "Show Less" : "Show All"}
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${showAll ? "rotate-180" : ""}`}
                />
              </button>
            )}
          </div>
        ) : (
          <div className="text-center text-gray-500">No results yet.</div>
        )}
      </div>

      <UploadDocumentModal
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
        onUploaded={loadDocuments}
      />
    </>
  );
};
